import classNames from "classnames";
import { Field, Form, Formik } from "formik";
import * as yup from "yup";
import { ButtonSpinner } from "../../components/atoms/Button/ButtonSpinner";
import Input from "../../components/atoms/Input";
import {
  FieldError,
  FileUploadFields,
  ONE_MB,
  StepSection,
  TEN_MB,
  validateFileSize,
} from "../../components/features/MultiSectionBatches/DrillFormComponents";

import { useDownloadDrillTemplate } from "../../components/features/MultiSectionBatches/useDownloadDrillTemplate";
import { useFetchAdminSections } from "../../hooks/http/useFetchAdminSections";
import OperatorInterface from "../../interfaces/OperatorInterface";
import { Layout } from "./Layout";
import { UploadDrillFormValues, useUploadDrill } from "./useUploadDrill";

const initialValues: UploadDrillFormValues = {
  drillName: "",
  csvFile: null,
  drillImage: null,
  contentImagesZip: null,
  contentSoundsZip: null,
  contentPdfsZip: null,
};

const validationSchema = yup.object().shape({
  drillName: yup
    .string()
    .required("教材名を入力してください")
    .max(512, "教材名は512文字以内で入力してください"),
  csvFile: yup.mixed().required("CSVファイルは必須です"),
  contentImagesZip: yup
    .mixed()
    .test(
      "fileSize",
      "ファイルサイズは1MB以下にしてください",
      validateFileSize(ONE_MB),
    ),
  contentSoundsZip: yup
    .mixed()
    .test(
      "fileSize",
      "ファイルサイズは10MB以下にしてください",
      validateFileSize(TEN_MB),
    ),
  contentPdfsZip: yup
    .mixed()
    .test(
      "fileSize",
      "ファイルサイズは10MB以下にしてください",
      validateFileSize(TEN_MB),
    ),
});

type Props = {
  currentOperator: OperatorInterface;
};

export const NewPage = ({ currentOperator }: Props) => {
  const { isPending, handleSubmit } = useUploadDrill();
  const { downloadTemplate, isDownloading } = useDownloadDrillTemplate();
  const { data, isLoading } = useFetchAdminSections(currentOperator.id);
  const hasNoAdminSections = !isLoading && (data?.sections ?? []).length === 0;

  if (isLoading) {
    return (
      <Layout activeTab="new">
        <p className="mt-[24px]">読み込み中...</p>
      </Layout>
    );
  }

  if (hasNoAdminSections) {
    return (
      <Layout activeTab="new">
        <p className="mt-[24px] text-red-400">
          管理者権限を持つ校舎がありません。
        </p>
      </Layout>
    );
  }

  return (
    <Layout activeTab="new">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, setFieldTouched, isSubmitting, isValid }) => (
          <Form className="mt-[24px] flex flex-col gap-4">
            <StepSection>
              <p className="text-[14px]">
                1. 登録するデータの入力テンプレートをダウンロード
              </p>
              <div className="mt-[20px]">
                <button
                  type="button"
                  className="buiButtonBase buiButtonVariantOutline buiButtonSizeSm buiButtonRound px-[32px]"
                  disabled={isDownloading}
                  onClick={downloadTemplate}
                >
                  CSVダウンロード
                </button>
              </div>
            </StepSection>

            <StepSection>
              <p className="text-[14px]">2. 登録する教材名を入力</p>
              <div className="mt-[20px] flex flex-col gap-1">
                <label className="text-[12px] font-normal">
                  教材名<span className="text-red-400">*</span>
                </label>
                <Field
                  name="drillName"
                  component={Input}
                  placeholder="教材名を入力"
                />
                <FieldError name="drillName" />
              </div>
            </StepSection>

            <StepSection>
              <div className="flex flex-col gap-1">
                <p className="text-[14px]">
                  3.
                  情報を保存したCSVファイルとコンテンツ（画像・音声・PDFなど）のZIPファイルをアップロード
                </p>
                <p className="text-[13px] text-black">
                  ※エクセル形式ではインポートできませんので、必ずCSV形式で保存してください。
                </p>
              </div>
              <FileUploadFields
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
              />
            </StepSection>

            <div className="mt-[16px] flex justify-end">
              <button
                type="submit"
                disabled={isPending || isSubmitting || !isValid}
                className={classNames(
                  "buiButtonBase buiButtonVariantFilled buiButtonSizeMd py-[12px]",
                  {
                    "opacity-50": isPending || isSubmitting || !isValid,
                  },
                )}
              >
                {isPending || isSubmitting ? <ButtonSpinner /> : "登録"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </Layout>
  );
};
